/* eslint-disable @typescript-eslint/no-explicit-any */
import { ItemStatus } from '../../../interfaces/enums'
import { CartListProps } from './CartList.types'
import {
  CartHeadingButton,
  CartHeadingButtonWrapper,
  CartListWrapper,
  CartListHeader,
  CartHeading
} from './CartList.styles'
import Message from '../Message'
import CartListGroup from './CartListGroup'
import { mergeCartListProps } from './CartList.utils'
import { IActions } from '../../../interfaces/actions'
import useId from '../../hooks/useId'

const CartList = (props: CartListProps) => {
  const cartProps = mergeCartListProps(props)
  const {
    cart = {
      subtotal: 0,
      total: 0,
      items: [],
      groups: []
    },
    status = ItemStatus.OK,
    heading,
    emptyMessage,
    errorMessage,
    loadingMessage,
    styles,
    price,
    itemCard,
    labelMap,
    groupHeading,
    groupIcon,
    groupTotal,
    groupUnitsLabel,
    groupUnitsLabelSingular,
    className,
    'data-testid': testid = 'cart',
    ...otherProps
  } = cartProps
  const {
    buttons: headingButtons = [],
    ...headingProps
  } = heading || {}
  const { getTestId } = useId(testid)
  const groupProps = {
    itemCard,
    price,
    labelMap,
    groupUnitsLabel,
    groupUnitsLabelSingular,
    groupHeading,
    groupIcon,
    groupTotal
  }
  const { items = [], groups = [] } = cart
  const itemsEmpty = !items?.length
  const groupsEmpty = !groups?.length
  const isEmpty = itemsEmpty && groupsEmpty

  const handleClick = (event: any, actions?: IActions) => {
    event.preventDefault()
    event.stopPropagation()
    if (actions) {
      actions({
        data: cart
      })
    }
  }

  return (
    <>
      {!isEmpty && status !== ItemStatus.LOADING && (
        <CartListWrapper
          className={`CartList ${className || ''}`}
          gap={18}
          sx={styles}
          data-testid={getTestId()}
          {...otherProps}>
          {heading && (
            <CartListHeader
              className="CartListHeader"
              data-testid={getTestId('header')}>
              <CartHeading
                data-testid={getTestId('heading')}
                {...headingProps}
              />
              {!!headingButtons.length && (
                <CartHeadingButtonWrapper
                  className="CartListButtonWrapper"
                  data-testid={getTestId('header-buttons')}>
                  {headingButtons.map(
                    (
                      {
                        actions: buttonActions,
                        'data-testid': buttonTestId,
                        ...buttonProps
                      },
                      index
                    ) => (
                      <CartHeadingButton
                        key={buttonProps.label}
                        className="HeadingButton"
                        data-testid={getTestId(buttonTestId || `${index}`)}
                        onClick={(event) => handleClick(event, buttonActions)}
                        {...buttonProps}
                      />
                    )
                  )}
                </CartHeadingButtonWrapper>
              )}
            </CartListHeader>
          )}
          {!itemsEmpty && groupsEmpty && (
            <CartListGroup
              items={items}
              data-testid={getTestId('group')}
              {...groupProps}
            />
          )}
          {groups?.map((group) => {
            const groupKey = group.groupBy.join('-')
            return (
              <CartListGroup
                key={groupKey}
                data-testid={getTestId(groupKey)}
                {...group}
                {...groupProps}
              />
            )
          })}
        </CartListWrapper>
      )}
      {isEmpty && status === ItemStatus.OK && emptyMessage && (
        <Message data-testid={getTestId('empty-message')} {...emptyMessage} />
      )}
      {isEmpty && status === ItemStatus.ERROR && errorMessage && (
        <Message data-testid={getTestId('error-message')} {...errorMessage} />
      )}
      {status === ItemStatus.LOADING && loadingMessage && (
        <Message
          data-testid={getTestId('loading-message')}
          {...loadingMessage}
        />
      )}
    </>
  )
}

export default CartList
